import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Cancel as CancelIcon } from '@mui/icons-material';
import { ReactComponent as CheckMarkIcon } from "../icons/CheckMarkIcon.svg";
import { BusinessFormData, MyBusinessSchema, ReferralChannel, ResourceBookingSchema, ResourceSchema, ResourceType } from '../data/types';
import { useCompleteSignIn, useCreateReferral, useLoadUserResourceBooking } from '../hooks/authhooks';
import { useAllBusinesses, useUpdateBusinessDetails } from '../hooks/businesshooks';
import {
  FINANCIAL_WHEEL_LABELS,
  buildConsentedReferralRequest,
  getPartnerDisplayName,
  qualifyFinancialResource,
} from '../utils/fundingPartners';
import {
  buildBusinessReferralSnapshot,
  formatBusinessLocation,
  mapBusinessFormToSchema,
  mapBusinessStageToQualification,
} from '../utils/businessProfile';
import SubmitRequestButton from './SubmitRequestButton';
import FormProgressBar from './FormProgressBar';
import DesktopAddBusiness from '../molecules/Desktop/AddBusiness';
import MobileAddBusiness from '../molecules/Mobile/AddBusiness';

const CHANNELS: ReferralChannel[] = ['pantry', 'joint_seminar', 'incubator', 'owned_digital', 'print'];

/** Business profile, fit details, then consent review. */
const CHECK_FIT_TOTAL_STEPS = 3;

const parseChannel = (value: string | null): ReferralChannel => {
  if (value && CHANNELS.includes(value as ReferralChannel)) return value as ReferralChannel;
  return 'pantry';
};

type CheckFitFundingFormProps = {
  resourceDetail: ResourceSchema
  handleClose: () => void
  handleSubmitRequest?: (resource: ResourceSchema, resourceType: ResourceType, booking: ResourceBookingSchema) => Promise<boolean> | boolean | void
  variant?: 'desktop' | 'mobile'
}

/** Consent-gated check-fit + referral handoff for financial-intelligence listings. */
const CheckFitFundingForm: React.FC<CheckFitFundingFormProps> = ({
  resourceDetail,
  handleClose,
  variant = 'desktop',
}) => {

  const navigate = useNavigate();
  const isMobile = variant === 'mobile';
  const [searchParams] = useSearchParams();
  const channel = parseChannel(searchParams.get('channel'));
  const seminarId = searchParams.get('seminar') || '';
  const { user } = useCompleteSignIn();
  const loggedinUid = user?.uid || '';
  const username = user?.username || user?.email || '';
  const { userResourceBooking, reloadUserResourceBooking } = useLoadUserResourceBooking(resourceDetail?.community_id || '', resourceDetail?.id || '', loggedinUid);
  const { submitReferral, isSubmittingReferral } = useCreateReferral(
    loggedinUid,
    resourceDetail?.community_id || '',
    resourceDetail?.id || ''
  );
  const { businesses, reloadAllBusinesses, isLoadingAllBusinesses } = useAllBusinesses(loggedinUid);
  const { addBusinessDetails } = useUpdateBusinessDetails(loggedinUid);
  const [step, setStep] = useState<'business' | 'fit' | 'consent'>('business');
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [creatingBusiness, setCreatingBusiness] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [commerciallyActive, setCommerciallyActive] = useState<boolean | undefined>(undefined);
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [unqualifiedReason, setUnqualifiedReason] = useState('');
  const isSubmitting = isSubmittingReferral;
  const submitLock = useRef(false);
  const [handoffUrl, setHandoffUrl] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [fitPreview, setFitPreview] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  const partnerName = getPartnerDisplayName(resourceDetail);
  const privacyUrl = resourceDetail.funding_profile?.partner?.privacy_url || 'https://airiwheels.ai/privacy';
  const eligibility = resourceDetail.funding_profile?.eligibility || {};
  const profiles = useMemo(() => businesses || [], [businesses]);
  const selectedBusiness = useMemo(
    () => profiles.find((item) => item.id === selectedBusinessId) || (profiles.length === 1 ? profiles[0] : undefined),
    [profiles, selectedBusinessId]
  );
  const snapshot = selectedBusiness ? buildBusinessReferralSnapshot(selectedBusiness) : null;

  useEffect(() => {
    if (!loggedinUid) return;
    void reloadAllBusinesses();
  }, [loggedinUid, reloadAllBusinesses]);

  useEffect(() => {
    if (isLoadingAllBusinesses) return;
    if (profiles.length === 1 && profiles[0].id) {
      setSelectedBusinessId(profiles[0].id);
      setCreatingBusiness(false);
      setStep((prev) => (prev === 'business' ? 'fit' : prev));
    } else if (profiles.length === 0) {
      setCreatingBusiness(true);
      setStep('business');
    }
  }, [isLoadingAllBusinesses, profiles]);

  const wrapperClass = isMobile ? 'w-full' : 'w-11/12';

  const alreadySubmitted = Boolean(userResourceBooking?.status && userResourceBooking.status !== 'canceled');
  const hubPath = userResourceBooking?.business_id
    ? `/business/${userResourceBooking.business_id}`
    : selectedBusiness?.id
      ? `/business/${selectedBusiness.id}`
      : '/business';
  const activeStep = (alreadySubmitted || isComplete || step === 'consent')
    ? CHECK_FIT_TOTAL_STEPS
    : step === 'fit'
      ? 2
      : 1;

  const handleContinueToConsent = () => {
    if (!selectedBusiness) return;
    const qualification = qualifyFinancialResource({
      commercially_active: commerciallyActive,
      business_stage: snapshot?.business_stage,
      requires_active_sales: eligibility.requires_active_sales,
      excluded_stages: eligibility.excluded_stages || ['idea'],
    });
    if (!qualification.qualified) {
      setUnqualifiedReason(qualification.reason);
      return;
    }
    setFitPreview(qualification.reason);
    setStep('consent');
  };

  const handleSubmit = async () => {
    if (submitLock.current || isSubmitting || !resourceDetail?.resource_type || !selectedBusiness || !snapshot) return;
    if (!consentGranted) {
      setConsentError(true);
      return;
    }
    const qualification = qualifyFinancialResource({
      commercially_active: commerciallyActive,
      business_stage: snapshot.business_stage,
      requires_active_sales: eligibility.requires_active_sales,
      excluded_stages: eligibility.excluded_stages || ['idea'],
    });
    if (!qualification.qualified) {
      setUnqualifiedReason(qualification.reason);
      return;
    }
    submitLock.current = true;
    try {
      const referral = await submitReferral(buildConsentedReferralRequest({
        consent: consentGranted,
        commercially_active: commerciallyActive === true,
        ...snapshot,
        name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || username,
        email: user?.email || username,
        channel,
        seminar_id: seminarId || undefined,
        reason: qualification.reason,
      }));
      if (!referral?.id) {
        submitLock.current = false;
        return;
      }
      setHandoffUrl(referral.signup_url || '');
      setDeliveryStatus(referral.sync?.delivery_status || 'queued');
      setFitPreview(referral.qualification?.fit_brief || qualification.reason);
      setIsComplete(true);
      await reloadUserResourceBooking();
    } catch {
      submitLock.current = false;
    }
  };

  const handleCreateBusiness = async (data: BusinessFormData) => {
    const saved = await addBusinessDetails(
      mapBusinessFormToSchema(data, username, { uid: loggedinUid }),
      data.logoFile
    ) as MyBusinessSchema | null;
    await reloadAllBusinesses();
    if (saved?.id) {
      setSelectedBusinessId(saved.id);
      setCreatingBusiness(false);
      setStep('fit');
    }
  };

  const wheelLabels = useMemo(
    () => (resourceDetail.funding_profile?.wheels || []).map((wheel) => FINANCIAL_WHEEL_LABELS[wheel] || wheel),
    [resourceDetail.funding_profile?.wheels]
  );
  const sharedFields = [
    ['Contact name', `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || username],
    ['Email', user?.email || username],
    ['Business name', snapshot?.business_name || ''],
    ['Business type', snapshot?.business_type || ''],
    ['Business stage', snapshot?.business_stage || ''],
    ['City', snapshot?.city || ''],
    ['County', snapshot?.county || ''],
    ['State', snapshot?.state || ''],
    ['ZIP', snapshot?.zipcode || ''],
    ['Currently generating sales', commerciallyActive ? 'Yes' : 'No'],
    ['Qualification result', fitPreview],
    ['Referral channel', channel.replace('_', ' ')],
    ...(seminarId ? [['Seminar context', seminarId]] : []),
    ['Consent record', 'Granted only when checked; identity, business profile, and qualification scopes; version 2026-09-01'],
  ];
  const AddBusinessForm = isMobile ? MobileAddBusiness : DesktopAddBusiness;
  const isCreatingBusiness = !isLoadingAllBusinesses && (creatingBusiness || profiles.length === 0);

  return (
    <div className={`flex flex-col justify-evenly items-center ${isMobile ? 'h-full min-h-0 justify-start' : 'h-full'}`} data-testid="check-fit-form">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
        <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <FormProgressBar
        totalSteps={CHECK_FIT_TOTAL_STEPS}
        currentStep={activeStep}
        variant={variant}
        fillClassName="bg-primary-400"
      />

      {alreadySubmitted ? (
        <div className="flex flex-col items-center justify-center w-full py-8">
          <div className="w-16 h-16 mb-6 rounded-full bg-primaryDark-400 flex items-center justify-center">
            <CheckMarkIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-ink font-semibold text-[24px] font-raleway text-center mb-2 mt-2">Request already submitted</h2>
          <p className="text-[#3F5B64] text-[16px] font-open-sans text-center mb-6">
            You have already submitted a request for <span className="font-semibold">{resourceDetail.name}</span> on <span className='font-light'>{userResourceBooking?.created ? moment(userResourceBooking?.created).format('MMM DD, YYYY') : ''}</span>.
          </p>
          <button
            onClick={() => {
              handleClose();
              navigate(hubPath);
            }}
            className="py-3 px-8 font-semibold text-[16px] sm:text-[18px] font-raleway border-0 bg-primaryDark-0 text-white rounded-lg hover:bg-primaryDark-300 transition-colors touch-manipulation"
          >
            Go to My Business
          </button>
        </div>
      ) : unqualifiedReason ? (
        <div className={`${wrapperClass} py-6`} data-testid="check-fit-unqualified">
          <p className="page-kicker">Fit check</p>
          <h2 className="text-ink font-semibold text-[22px] font-raleway mb-2 mt-2">Not a fit yet</h2>
          <p className="text-[#3F5B64] font-open-sans text-[15px] mb-4">{unqualifiedReason}</p>
          <p className="text-[#3F5B64] font-open-sans text-[15px] mb-6">
            Keep building on HomeCook. When you have active sales, come back and we can intro you to {partnerName}.
          </p>
          <button type="button" className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-6 font-bold font-raleway text-white" onClick={handleClose}>
            Back to pantry
          </button>
        </div>
      ) : isComplete ? (
        <div className={`${wrapperClass} py-6`} data-testid="check-fit-success">
          <p className="page-kicker">You are a fit</p>
          <h2 className="text-ink font-semibold text-[22px] font-raleway mb-2 mt-2">Continue to {partnerName}</h2>
          <p className="text-[#3F5B64] font-open-sans text-[15px] mb-4">
            {deliveryStatus === 'delivered'
              ? `Your intro was sent to ${partnerName}.`
              : `Your intro is queued. You can still start on ${partnerName} with your referral code.`}
          </p>
          {resourceDetail.funding_profile?.partner?.slug === 'airiwheels' && (
            <p className="rounded-lg bg-primary-tint p-3 text-sm font-open-sans text-[#3F5B64] mb-4">
              AiriWheels typically starts setup with your menu and purchase receipts; dish photos can
              improve model setup. You provide those directly to AiriWheels, they are not HomeCook
              compliance or approval requirements. Menu, supplies, and sales sharing can be turned on later from My Business.
            </p>
          )}
          {fitPreview && <p className="text-sm font-open-sans text-[#3F5B64] mb-4">{fitPreview}</p>}
          {handoffUrl && (
            <button
              type="button"
              data-testid="continue-to-partner"
              className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-6 font-bold font-raleway text-white mb-3"
              onClick={() => window.open(handoffUrl, '_blank', 'noopener,noreferrer')}
            >
              Continue to {partnerName}
            </button>
          )}
        </div>
      ) : (
        <>
          <p className={`page-kicker ${wrapperClass} text-left`}>
            {step === 'business' ? 'Step 1 of 3 · Business' : step === 'fit' ? 'Step 2 of 3 · Fit' : 'Step 3 of 3 · Consent'}
          </p>
          <p className={`text-ink font-semibold text-[22px] font-raleway text-left ${wrapperClass} mt-1`}>
            Get started for <span className="font-bold">{resourceDetail.name}</span>
          </p>
          <p className={`text-left text-[15px] my-2 font-open-sans text-[#3F5B64] ${wrapperClass}`}>
            {step === 'business'
              ? (isCreatingBusiness
                ? `Add a business for this referral. We'll use this profile for the ${partnerName} intro.`
                : 'Choose the business this referral belongs to, or create one first.')
              : `Confirm you are commercially active, then share a few details with ${partnerName}.`}
          </p>
          {wheelLabels.length > 0 && step !== 'business' && (
            <div className={`${wrapperClass} flex flex-wrap gap-2 mb-3`}>
              {wheelLabels.map((label) => (
                <span key={label} className="text-xs font-raleway bg-primary-tint text-primaryDark-0 rounded-full px-2.5 py-1">{label}</span>
              ))}
            </div>
          )}
          {step === 'business' ? (
            <div className={`border border-[#E4EEF1] p-4 sm:p-5 rounded-2xl ${wrapperClass} bg-white`} data-testid="check-fit-business-step">
              {isLoadingAllBusinesses ? (
                <div className="flex items-center justify-center py-10" data-testid="check-fit-businesses-loading" role="status">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primaryDark-400" />
                </div>
              ) : isCreatingBusiness ? (
                <>
                  <p className="text-ink font-semibold text-[16px] font-raleway mb-1">Add a business for this referral</p>
                  <p className="text-sm font-open-sans text-[#3F5B64] mb-4">
                    We’ll use this profile for the {partnerName} intro.
                  </p>
                  <AddBusinessForm
                    embedded
                    showIntro={false}
                    setShowIntro={setShowIntro}
                    handleClose={() => {
                      if (profiles.length > 0) setCreatingBusiness(false);
                      else handleClose();
                    }}
                    handleSubmitForm={handleCreateBusiness}
                    editingBusiness={null}
                  />
                </>
              ) : (
                <>
                  <p className="text-ink font-semibold text-[16px] font-raleway mb-3">Select a business</p>
                  <div className="space-y-2" data-testid="check-fit-business-list">
                    {profiles.map((business) => (
                      <label key={business.id} className={`flex items-start gap-3 rounded-lg border p-3 ${selectedBusinessId === business.id ? 'border-primaryDark-0 bg-primary-tint' : 'border-[#E4EEF1]'}`}>
                        <input
                          type="radio"
                          name="check-fit-business"
                          checked={selectedBusinessId === business.id}
                          onChange={() => setSelectedBusinessId(business.id || '')}
                        />
                        <span>
                          <span className="block font-semibold text-ink">{business.business_name}</span>
                          <span className="block text-sm text-[#3F5B64]">{business.business_type} · {formatBusinessLocation(business)}</span>
                          <span className="block text-xs text-[#3F5B64]">{mapBusinessStageToQualification(business.business_stage)}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="mt-3 text-sm font-semibold text-primaryDark-0"
                    onClick={() => { setCreatingBusiness(true); setShowIntro(false); }}
                  >
                    Create a new business
                  </button>
                </>
              )}
            </div>
          ) : step === 'fit' ? (
            <div className={`border border-[#E4EEF1] p-4 sm:p-5 rounded-2xl ${wrapperClass} bg-white`}>
              {selectedBusiness && (
                <div className="rounded-lg bg-primary-tint p-3 mb-3 text-sm font-open-sans text-[#3F5B64]" data-testid="check-fit-selected-business">
                  <p className="font-semibold text-ink">{selectedBusiness.business_name}</p>
                  <p>{selectedBusiness.business_type} · {formatBusinessLocation(selectedBusiness)}</p>
                </div>
              )}
              <p className="text-ink font-semibold text-[16px] font-raleway mt-2">Do you currently generate sales?</p>
              <div className="flex flex-wrap gap-4 my-3">
                <label className="inline-flex items-center gap-2 font-open-sans text-sm">
                  <input type="radio" name="commercially_active" checked={commerciallyActive === true} onChange={() => setCommerciallyActive(true)} data-testid="commercially-active-yes" /> Yes
                </label>
                <label className="inline-flex items-center gap-2 font-open-sans text-sm">
                  <input type="radio" name="commercially_active" checked={commerciallyActive === false} onChange={() => setCommerciallyActive(false)} data-testid="commercially-active-no" /> No
                </label>
              </div>
            </div>
          ) : (
            <div className={`border border-[#E4EEF1] p-4 sm:p-5 rounded-2xl ${wrapperClass} bg-white`} data-testid="consent-step">
              {fitPreview && <p className="font-open-sans text-sm text-[#3F5B64] mb-3">{fitPreview}</p>}
              <p className="font-semibold font-raleway text-ink mb-2">Information shared with {partnerName}</p>
              <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-x-3 gap-y-1 text-sm font-open-sans mb-4" data-testid="shared-field-list">
                {sharedFields.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt className="font-semibold text-ink">{label}</dt>
                    <dd className="text-[#3F5B64] break-words">{value || 'Not provided'}</dd>
                  </React.Fragment>
                ))}
              </dl>
              <div className="rounded-lg bg-gray-50 p-3 mb-3 text-sm font-open-sans" data-testid="not-shared">
                <p className="font-semibold text-ink">Not shared</p>
                <p className="text-[#3F5B64]">
                  Financial statements, bank or POS data, inventory, menu or margin data, diner data, kitchen bookings,
                  payouts, compliance documents, and internal notes.
                </p>
              </div>
              <label className="flex items-start gap-2 mt-2 font-open-sans text-sm" data-testid="consent-row">
                <input type="checkbox" checked={consentGranted} onChange={(e) => { setConsentGranted(e.target.checked); setConsentError(false); }} data-testid="consent-checkbox" />
                <span>
                  I authorize HomeCook to share the information shown above with {partnerName} for this referral.
                  {' '}<a href={privacyUrl} target="_blank" rel="noreferrer" className="text-primaryDark-0 underline">Privacy policy</a>
                </span>
              </label>
              {consentError && <p className="text-red-500 text-sm mt-2" data-testid="consent-required">Consent is required before we share your details.</p>}
            </div>
          )}
          {!(step === 'business' && (isLoadingAllBusinesses || isCreatingBusiness)) && (
          <div className={`flex flex-row my-4 ${wrapperClass} ${isMobile ? 'gap-3' : ''}`}>
            <button
              type="button"
              className={`${isMobile ? 'flex-1 py-3' : 'py-2 w-[217px] mr-auto'} h-12 font-bold text-[18px] font-raleway text-primaryDark-0 border-[#E4EEF1] border-[1px] rounded-lg`}
              data-testid="cancel-button"
              onClick={step === 'consent' ? () => setStep('fit') : step === 'fit' ? () => setStep('business') : handleClose}
            >
              {step === 'fit' || step === 'consent' ? 'Back' : 'Cancel'}
            </button>
            {step === 'business' ? (
              <button
                type="button"
                className={`${isMobile ? 'flex-1 py-3' : 'py-2 w-[217px] ml-auto'} h-12 font-bold text-[18px] font-raleway border-0 text-white rounded-lg ${!selectedBusinessId ? 'bg-gray-300' : 'bg-primaryDark-0'}`}
                disabled={!selectedBusinessId}
                onClick={() => setStep('fit')}
                data-testid="check-fit-select-business"
              >
                Continue
              </button>
            ) : step === 'fit' ? (
              <button
                type="button"
                className={`${isMobile ? 'flex-1 py-3' : 'py-2 w-[217px] ml-auto'} h-12 font-bold text-[18px] font-raleway border-0 text-white rounded-lg ${(!selectedBusiness || commerciallyActive === undefined) ? 'bg-gray-300' : 'bg-primaryDark-0'}`}
                disabled={!selectedBusiness || commerciallyActive === undefined}
                onClick={handleContinueToConsent}
                data-testid="check-fit-continue"
              >
                Continue
              </button>
            ) : (
              <SubmitRequestButton
                isSubmitting={isSubmitting}
                disabled={!selectedBusiness || commerciallyActive === undefined}
                onClick={handleSubmit}
                label="Get Started"
                className={`${isMobile ? 'flex-1 py-3' : 'py-2 w-[217px] ml-auto'} h-12 font-bold text-[18px] font-raleway border-0 text-white rounded-lg ${(!selectedBusiness || commerciallyActive === undefined) ? 'bg-gray-300' : 'bg-primaryDark-0'}`}
                data-testid="check-fit-submit"
              />
            )}
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default CheckFitFundingForm;
